// Copyright 2025, University of Maryland, All Rights Reserved
import React, { ReactNode, createContext, useContext } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { JobsComponent as JobsUI } from './components/jobs/JobsComponent';
import { JobsComponentProps } from './types';
import { AxiosInstance } from 'axios';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create a simple API context for internal use
type ApiContextType = {
  apiInstance: AxiosInstance;
};

export const ApiContext = createContext<ApiContextType | null>(null);

// Hook to use the API context
export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within JobsComponent');
  }
  return context;
};

interface AuthErrorHandlerProps {
  children: ReactNode;
}

// Create a wrapper component for auth handling
const AuthErrorHandler = ({ children }: AuthErrorHandlerProps) => {
  // Note: Auth error handling is now managed by the centralized API manager
  return <>{children}</>;
};

/**
 * Main Jobs Component
 * Accepts a pre-configured API instance via props
 */
export const JobsComponent: React.FC<JobsComponentProps> = ({
  org_id,
  ws_id,
  isGlobalAdmin = false,
  metadata_fields,
  apiInstance,
}: JobsComponentProps) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ApiContext.Provider value={{ apiInstance }}>
        <AuthErrorHandler>
          <JobsUI
            org_id={org_id}
            ws_id={ws_id}
            isGlobalAdmin={isGlobalAdmin}
            metadata_fields={metadata_fields}
          />
        </AuthErrorHandler>
      </ApiContext.Provider>
    </QueryClientProvider>
  );
};
